import type { Result } from "@pdfking/errors"
import { Failure, ResultMap, Success } from "@pdfking/errors"
import { Divide, Max, Min, Mult, Subtract, Total } from "@pdfking/math"
import { NullQ } from "@pdfking/structs"
import { DeleteDuplicates, FullMethodDispatcher, Items, Map, Zip } from "@pdfking/tfunc"

import type { MathError } from "#public"
import type { Math, Quantity } from "#public"

import { Operation } from "../types/Operation"
import { QuantityToString } from "./QuantityToString"

const formatter = (values: number[]): string => QuantityToString([...DeleteDuplicates(values)])

type Eval = Result<Quantity, MathError>
type Ctx = Record<string, Quantity>

// Combine two evaluated operands element-wise. `expOf` derives the new exponent
// (add for `*`, subtract for `/`) or returns `undefined` for an illegal pairing
// (add/sub of mismatched exponents). A `divide` flag guards against zero divisors.
const combine = (
  evaluate: (node: Math, ctx: Ctx) => Eval,
  node: { left: Math; right: Math },
  ctx: Ctx,
  expOf: (l: number, r: number) => number | undefined,
  op: (a: number, b: number) => number,
  divide = false
): Eval =>
  ResultMap(evaluate(node.left, ctx), l =>
    ResultMap(evaluate(node.right, ctx), r => {
      const exp = expOf(l.exp, r.exp)
      if (NullQ(exp))
        return Failure("QUANTITY_EXP_ERROR", {
          exp: r.exp,
          min: l.exp,
          max: l.exp,
          value: formatter(r.vars),
        })
      if (divide && r.vars.some(v => v === 0))
        return Failure("QUANTITY_DIVIDE_ZERO_ERROR", { input: node })
      return Success<Quantity, MathError>({
        enum: Operation.NUM,
        exp,
        vars: [...Map(([a, b]) => op(a, b), Zip(l.vars, r.vars))],
      })
    })
  )

const evaluate: (node: Math, ctx: Ctx) => Eval = FullMethodDispatcher<[Math, Ctx], Eval>({
  [Operation.NUM]: node => Success<Quantity, MathError>(node),
  [Operation.SYM]: (node, ctx) => {
    const q = ctx[node.name]
    return q ? Success<Quantity, MathError>(q) : Failure("QUANTITY_VAR_ERROR", { name: node.name })
  },
  [Operation.NEG]: (node, ctx) =>
    ResultMap(evaluate(node.var, ctx), q =>
      Success<Quantity, MathError>({
        ...q,
        vars: q.vars.map(v => -v),
      })
    ),
  [Operation.ADD]: (node, ctx) =>
    combine(evaluate, node, ctx, (l, r) => (l === r ? l : undefined), Total),
  [Operation.SUB]: (node, ctx) =>
    combine(evaluate, node, ctx, (l, r) => (l === r ? l : undefined), Subtract),
  [Operation.MUL]: (node, ctx) => combine(evaluate, node, ctx, (l, r) => l + r, Mult),
  [Operation.DIV]: (node, ctx) => combine(evaluate, node, ctx, (l, r) => l - r, Divide, true),
  [Operation.MIN]: (node, ctx) =>
    combine(evaluate, node, ctx, (l, r) => (l === r ? l : undefined), Min),
  [Operation.MAX]: (node, ctx) =>
    combine(evaluate, node, ctx, (l, r) => (l === r ? l : undefined), Max),
  [Operation.WITH]: (node, ctx) => {
    const extended = { ...ctx }
    for (const [name, subnode] of Items(node.vars)) {
      const result = evaluate(subnode, extended)
      if (result.error) return result
      extended[name] = result.value
    }
    return evaluate(node.node, extended)
  },
})

// Evaluate a parsed `Math` tree to a single `Quantity`, or fail with a
// `MathError`. `context` seeds named variables for `VAR`/`WITH` nodes.
// The final exponent must fall within `[min, max]` (default `[0, 1]`).
export const EvaluateMath = (
  node: Math,
  opts: {
    min?: number
    max?: number
    context?: Record<string, Quantity>
  } = {}
): Result<Quantity, MathError> => {
  const { min = 0, max = 1, context = {} } = opts
  return ResultMap(evaluate(node, context), q =>
    q.exp >= min && q.exp <= max
      ? Success<Quantity, MathError>(q)
      : Failure("QUANTITY_EXP_ERROR", {
          exp: q.exp,
          min,
          max,
          value: formatter(q.vars),
        })
  )
}
